<template>
  <span class="info-icon-wrap">
    <!-- Icono visible según criticidad -->
    <span
      v-if="mostrarIcono"
      role="button"
      :aria-label="'Información sobre ' + clave"
      :class="['info-icon', 'info-icon-' + criticidadNormal]"
      @click.stop="abrir"
    >i</span>

    <!-- Overlay modal custom (no f7-popup) para z-index alto + coexistir con popups F7.
         Se teleporta a document.body al abrir para escapar de contextos de
         apilamiento creados por tablas con overflow en pantallas padre. -->
    <div v-if="abierto" ref="overlay" class="info-overlay" @click.self="cerrar">
      <div class="info-modal" :class="{ maximizado: maximizado }" @click.stop>
        <div class="info-header">
          <div class="info-titulo">
            <div class="info-titulo-principal">{{ tituloMostrado }}</div>
            <div class="info-titulo-sub" v-if="criticidadNormal">
              <span :class="'info-badge-crit info-badge-' + criticidadNormal">{{ criticidadNormal }}</span>
              <span v-if="conceptoFinal && conceptoFinal.clave" class="info-badge-clave">{{ conceptoFinal.clave }}</span>
            </div>
          </div>
          <div class="info-header-actions">
            <button
              v-if="esAdmin && modo === 'lectura' && conceptoFinal && conceptoFinal.id"
              class="info-editar"
              @click="iniciarEdicion"
              aria-label="Editar concepto"
              title="Editar"
            >&#9998; Editar</button>
            <button
              class="info-maximizar"
              @click="maximizado = !maximizado"
              :aria-label="maximizado ? 'Restaurar tamaño' : 'Pantalla completa'"
              :title="maximizado ? 'Restaurar' : 'Pantalla completa'"
            ><span v-html="maximizado ? '&#10532;' : '&#10530;'"></span></button>
            <button class="info-cerrar" @click="cerrar" aria-label="Cerrar">&times;</button>
          </div>
        </div>

        <div v-if="cargando" class="info-loading">Cargando…</div>
        <div v-else-if="!conceptoFinal" class="info-loading">Concepto no encontrado (clave: {{ clave }}).</div>

        <!-- Modo LECTURA -->
        <div v-else-if="modo === 'lectura'" class="info-body">
          <div class="info-seccion">
            <div class="info-seccion-titulo">Definición</div>
            <div class="info-seccion-texto">{{ conceptoFinal.texto || conceptoFinal.definicion || '—' }}</div>
          </div>

          <div v-if="conceptoFinal.formula" class="info-seccion">
            <div class="info-seccion-titulo">Fórmula</div>
            <pre class="info-formula">{{ conceptoFinal.formula }}</pre>
          </div>

          <div v-if="conceptoFinal.contextoUso" class="info-seccion">
            <div class="info-seccion-titulo">Contexto</div>
            <div class="info-seccion-texto">{{ conceptoFinal.contextoUso }}</div>
          </div>

          <div v-if="conceptoFinal.dependencias && conceptoFinal.dependencias.length" class="info-seccion">
            <div class="info-seccion-titulo">Relacionado con</div>
            <div class="info-badges">
              <span
                v-for="dep in conceptoFinal.dependencias"
                :key="dep"
                class="info-badge-dep"
                @click="irAConcepto(dep)"
              >{{ dep }}</span>
            </div>
          </div>
        </div>

        <!-- Modo EDICIÓN -->
        <div v-else class="info-body info-form">
          <div class="info-form-grid">
            <label>Título</label>
            <input type="text" v-model="conceptoEditable.titulo" />

            <label>Clave *</label>
            <input type="text" v-model="conceptoEditable.clave" placeholder="OEB_XXX" class="mono" />

            <label>Criticidad</label>
            <select v-model="conceptoEditable.criticidad">
              <option value="CRITICO">CRITICO</option>
              <option value="ALTO">ALTO</option>
              <option value="MEDIO">MEDIO</option>
              <option value="BAJO">BAJO</option>
            </select>

            <label class="top">Definición</label>
            <textarea v-model="conceptoEditable.texto" rows="3"></textarea>

            <label class="top">Fórmula</label>
            <textarea v-model="conceptoEditable.formula" rows="2" class="mono"></textarea>

            <label class="top">Contexto de uso</label>
            <textarea v-model="conceptoEditable.contextoUso" rows="2"></textarea>

            <label>Dependencias</label>
            <input
              type="text"
              :value="(conceptoEditable.dependencias || []).join(', ')"
              @input="conceptoEditable.dependencias = $event.target.value.split(',').map(function(s){return s.trim()}).filter(function(s){return s.length})"
              placeholder="OEB_DR, OEB_HD"
            />

            <label>Se edita en (pantalla)</label>
            <input type="text" v-model="conceptoEditable.editableEnPantalla" placeholder="Control Cifras OEB Planificación" />

            <label>Se edita en (ruta)</label>
            <input type="text" v-model="conceptoEditable.editableEnRuta" placeholder="/dashboard/oeb/planificacion/" class="mono" />

            <label>Activa</label>
            <label class="chk">
              <input type="checkbox" :checked="conceptoEditable.activa !== false" @change="conceptoEditable.activa = $event.target.checked" />
              Visible en iconos (i)
            </label>

            <label class="top">Pantallas donde aparece</label>
            <div class="info-pantallas-list">
              <label v-for="p in pantallasDisponibles" :key="p.id" class="chk">
                <input
                  type="checkbox"
                  :checked="(conceptoEditable.pantallas || []).indexOf(p.id) !== -1"
                  @change="togglePantallaConcepto(p.id, $event.target.checked)"
                />
                <span><code>{{ p.id }}</code> &mdash; {{ p.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Footer edición (estilos inline por inmunidad total a CSS global) -->
        <div
          v-if="modo === 'edicion' && conceptoFinal"
          class="info-footer info-footer-edit"
          style="display:flex !important;flex-direction:row !important;justify-content:flex-end;align-items:center;gap:10px;padding:12px 16px;background:#f8fafc;border-top:1px solid #e2e8f0;flex-shrink:0;width:100%;box-sizing:border-box"
        >
          <button class="info-btn-cancel" :disabled="guardando" @click="cancelarEdicion">Cancelar</button>
          <button class="info-btn-save" :disabled="guardando" @click="guardarEdicion">{{ guardando ? 'Guardando…' : 'Guardar' }}</button>
        </div>

        <!-- Footer lectura "Se edita en X" (estilos inline por inmunidad total) -->
        <div
          v-else-if="conceptoFinal && conceptoFinal.editableEnPantalla && conceptoFinal.editableEnRuta"
          class="info-footer"
          style="display:flex !important;flex-direction:row !important;justify-content:space-between;align-items:center;gap:12px;padding:12px 16px;background:#f8fafc;border-top:1px solid #e2e8f0;flex-shrink:0;width:100%;box-sizing:border-box"
        >
          <div
            class="info-footer-texto"
            style="flex:1 1 auto;font-size:12px;line-height:1.4;color:#475569;overflow-wrap:break-word;word-wrap:break-word;white-space:normal"
          >Se edita en <strong>{{ conceptoFinal.editableEnPantalla }}</strong></div>
          <button
            class="info-footer-btn"
            @click="irAPantalla"
            style="flex:0 0 auto;background:#1A2744;color:#fff;border:none;border-radius:6px;padding:8px 14px;font-size:12.5px;font-weight:600;cursor:pointer;white-space:nowrap"
          >Ir ahora →</button>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import Oea from '../js/db/Oea.js';
import Oeb from '../js/db/Oeb.js';
import { unwrapDto } from '../js/db/responseUtils.js';
import cerrojoDeLaSesion from '../js/utils/cerrojoDeLaSesion.js';

var ROLES_ADMIN = ['ADMIN', 'DIRECTOR', 'DTYCAL'];

export default {
  name: 'InfoIcon',
  props: {
    clave: { type: String, required: true },
    criticidad: { type: String, default: 'ALTO' },
    concepto: { type: Object, default: null },
    pantalla: { type: String, default: 'control-cifras-oeb-productividad' },
    dominio: {
      type: String,
      default: 'oeb',
      validator: function(v) { return v === 'oea' || v === 'oeb'; }
    }
  },
  data() {
    return {
      abierto: false,
      conceptoCargado: null,
      cargando: false,
      modo: 'lectura',
      conceptoEditable: null,
      guardando: false,
      maximizado: false,
      // Mismas 8 pantallas canónicas que el popup Guía grande.
      pantallasDisponibles: [
        { id: 'control-cifras-oeb-productividad', label: 'Productividad OEB' },
        { id: 'control-cifras-oeb-planificacion', label: 'Planificación OEB' },
        { id: 'control-cifras-oeb', label: 'Producción OEB' },
        { id: 'productividad-plan', label: 'Productividad Plan' },
        { id: 'planificacion-diaria', label: 'Planificación diaria' },
        { id: 'resumen-anual', label: 'Resumen anual' },
        { id: 'ejercicios', label: 'Ejercicios' },
        { id: 'configuracion', label: 'Configuración' }
      ]
    };
  },
  watch: {
    abierto(nuevo) {
      if (nuevo) {
        var self = this;
        this.$nextTick(function() { self.teleportAlBody(); });
      }
    }
  },
  beforeDestroy() {
    // Si al destruir el componente el overlay aún cuelga de body
    // (edge case: padre desmontado sin cerrar), lo eliminamos
    // manualmente para no dejar nodos huérfanos.
    var ov = this.$refs.overlay;
    if (ov && ov.parentNode === document.body) {
      document.body.removeChild(ov);
    }
  },
  computed: {
    adapter() {
      return this.dominio === 'oea' ? Oea : Oeb;
    },
    criticidadNormal() {
      var c = (this.criticidad || 'ALTO').toUpperCase();
      return ['CRITICO','ALTO','MEDIO','BAJO'].indexOf(c) >= 0 ? c : 'ALTO';
    },
    mostrarIcono() {
      return this.criticidadNormal === 'CRITICO' || this.criticidadNormal === 'ALTO';
    },
    conceptoFinal() {
      return this.conceptoCargado || this.concepto;
    },
    tituloMostrado() {
      if (this.conceptoFinal && this.conceptoFinal.titulo) return this.conceptoFinal.titulo;
      return this.clave;
    },
    esAdmin() {
      try {
        var raw = localStorage.getItem('user');
        if (!raw) return false;
        var u = JSON.parse(raw);
        if (!u || !Array.isArray(u.roles)) return false;
        for (var i = 0; i < u.roles.length; i++) {
          if (ROLES_ADMIN.indexOf(u.roles[i]) !== -1) return true;
        }
        return false;
      } catch (e) { return false; }
    }
  },
  methods: {
    abrir() {
      this.abierto = true;
      this.modo = 'lectura';
      if (!this.conceptoFinal) this.cargar();
      this.$emit('click');
    },
    cerrar() {
      this.abierto = false;
      // Al cerrar descartamos cualquier edición en curso no guardada.
      this.modo = 'lectura';
      this.conceptoEditable = null;
      this.maximizado = false;
    },
    teleportAlBody() {
      // Mueve el overlay a document.body para escapar de contextos de
      // apilamiento (overflow/transform de contenedores padre) que rompen
      // position:fixed y z-index. v-if ya se encarga de destruir el nodo
      // al cerrar → el append se hace una vez por apertura y es idempotente.
      var ov = this.$refs.overlay;
      if (!ov) return;
      // Bypass defensivo del enhancer global de popups F7 de app.js.
      // Aunque nuestro overlay no tiene class "popup" (no debería ser
      // detectado), marcamos ambos flags por si alguna ruta del enhancer
      // lo alcanza: data-popup-enhanced es la señal oficial (app.js L198),
      // data-ignore-enhance queda como marcador futuro.
      ov.setAttribute('data-popup-enhanced', 'true');
      ov.setAttribute('data-ignore-enhance', 'true');
      if (ov.parentNode !== document.body) {
        document.body.appendChild(ov);
      }
    },
    cargar() {
      var self = this;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que lo soltaba.
      cerrojoDeLaSesion(self, 'cargando');
      self.adapter.getConcepto(self.pantalla, self.clave)
        .then(function(res) {
          self.conceptoCargado = unwrapDto(res);
          self.cargando = false;
        })
        .catch(function() { self.cargando = false; });
    },
    iniciarEdicion() {
      if (!this.conceptoFinal) return;
      var src = this.conceptoFinal;
      // Clon aislado — mutar esto no toca el original hasta guardar.
      this.conceptoEditable = {
        id: src.id,
        titulo: src.titulo || '',
        clave: src.clave || '',
        texto: src.texto || '',
        formula: src.formula || '',
        contextoUso: src.contextoUso || '',
        dependencias: Array.isArray(src.dependencias) ? src.dependencias.slice() : [],
        editableEnPantalla: src.editableEnPantalla || '',
        editableEnRuta: src.editableEnRuta || '',
        criticidad: src.criticidad || 'ALTO',
        activa: src.activa !== false,
        orden: src.orden,
        tipo: src.tipo || 'concepto',
        pantallas: Array.isArray(src.pantallas) && src.pantallas.length
          ? src.pantallas.slice()
          : (src.pantalla ? [src.pantalla] : [this.pantalla])
      };
      this.modo = 'edicion';
    },
    cancelarEdicion() {
      this.conceptoEditable = null;
      this.modo = 'lectura';
    },
    togglePantallaConcepto(pantallaId, checked) {
      if (!this.conceptoEditable) return;
      var arr = Array.isArray(this.conceptoEditable.pantallas) ? this.conceptoEditable.pantallas.slice() : [];
      var idx = arr.indexOf(pantallaId);
      if (checked && idx === -1) arr.push(pantallaId);
      else if (!checked && idx !== -1) arr.splice(idx, 1);
      this.$set(this.conceptoEditable, 'pantallas', arr);
    },
    guardarEdicion() {
      var self = this;
      var e = self.conceptoEditable;
      if (!e || !e.id) return;
      if (!e.titulo || !e.titulo.trim()) {
        self._toast('El título no puede estar vacío');
        return;
      }
      if (!e.clave || !e.clave.trim()) {
        self._toast('La clave no puede estar vacía');
        return;
      }
      var pantallas = Array.isArray(e.pantallas) && e.pantallas.length ? e.pantallas : [self.pantalla];
      var body = {
        titulo: e.titulo,
        texto: e.texto,
        orden: e.orden,
        tipo: e.tipo || null,
        clave: e.clave || null,
        formula: e.formula || null,
        contextoUso: e.contextoUso || null,
        dependencias: e.dependencias || null,
        editableEnPantalla: e.editableEnPantalla || null,
        editableEnRuta: e.editableEnRuta || null,
        criticidad: e.criticidad || null,
        activa: e.activa !== false,
        pantallas: pantallas
      };
      cerrojoDeLaSesion(self, 'guardando');
      self.adapter.actualizarInstruccion(e.id, body)
        .then(function(res) {
          self.guardando = false;
          var actualizado = unwrapDto(res);
          // Refrescar el concepto visible con lo persistido.
          if (actualizado) {
            self.conceptoCargado = actualizado;
            // Si el padre nos pasó el concepto por prop, también lo sincronizamos
            // copiando campos sobre el objeto original (mutación controlada para
            // que la pantalla padre vea los cambios sin recargar).
            if (self.concepto) {
              var keys = ['titulo','texto','formula','contextoUso','dependencias',
                          'editableEnPantalla','editableEnRuta','criticidad','activa',
                          'pantalla','pantallas','clave','tipo','orden'];
              for (var i = 0; i < keys.length; i++) {
                self.concepto[keys[i]] = actualizado[keys[i]];
              }
            }
          }
          self.conceptoEditable = null;
          self.modo = 'lectura';
          self._toast('Guardado');
          self.$emit('guardado', actualizado);
        })
        .catch(function() {
          self.guardando = false;
          self._toast('Error al guardar');
        });
    },
    _toast(msg) {
      if (this.$f7 && this.$f7.toast && this.$f7.toast.create) {
        this.$f7.toast.create({ text: msg, position: 'top', closeTimeout: 1800 }).open();
      }
    },
    irAPantalla() {
      if (!this.conceptoFinal) return;
      var ruta = this.conceptoFinal.editableEnRuta;
      if (!ruta) return;
      this.cerrar();
      if (this.$f7router && this.$f7router.navigate) {
        this.$f7router.navigate(ruta);
      } else if (this.$f7 && this.$f7.views && this.$f7.views.main) {
        this.$f7.views.main.router.navigate(ruta);
      }
    },
    irAConcepto(dep) {
      this.$emit('navegar-concepto', dep);
    }
  }
};
</script>

<style scoped>
.info-icon-wrap { display: inline-flex; align-items: center; }
.info-icon {
  width: 13px; height: 13px; border-radius: 50%;
  color: #fff; font-weight: 700; font-style: italic; font-family: Georgia, serif; font-size: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; margin-left: 4px; vertical-align: middle;
  transition: transform .12s ease, box-shadow .12s ease;
  user-select: none;
}
.info-icon:hover { transform: scale(1.2); box-shadow: 0 2px 6px rgba(0,0,0,.25); }
.info-icon-CRITICO { background: #1A2744; }
.info-icon-ALTO    { background: #94a3b8; }

/* Overlay y modal.
   Nota: este componente usa <style scoped> pero aplicamos !important
   + selectores amplios para que las reglas sobrevivan aunque el overlay
   se teleporte a document.body y no herede los data-v-xxx de scoping. */
.info-overlay {
  position: fixed !important;
  top: 0 !important; left: 0 !important;
  right: 0 !important; bottom: 0 !important;
  width: 100vw; height: 100vh;
  background: rgba(0, 0, 0, 0.55) !important;
  z-index: 99999 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  /* reset herencia */
  text-transform: none !important;
  letter-spacing: normal !important;
  font-variant: normal !important;
  color: #1e293b;
  font-family: inherit;
}
.info-overlay *, .info-modal, .info-modal * {
  text-transform: none !important;
  letter-spacing: normal !important;
}
.info-overlay .info-seccion-titulo,
.info-overlay .info-badge-crit {
  text-transform: uppercase !important;
  letter-spacing: .5px !important;
}
.info-modal {
  width: 600px !important;
  max-width: 92vw !important;
  max-height: 92vh;
  background: #ffffff !important;
  border-radius: 10px;
  overflow: hidden;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35) !important;
  transition: width .25s ease, max-width .25s ease,
              max-height .25s ease, height .25s ease;
}
.info-modal.maximizado {
  width: 92vw !important;
  height: 92vh !important;
  max-width: 92vw !important;
  max-height: 92vh !important;
}
.info-header {
  background: #1A2744; color: #fff;
  padding: 14px 16px;
  display: flex; align-items: flex-start; gap: 10px;
}
.info-titulo { flex: 1; }
.info-titulo-principal { font-size: 15px; font-weight: 700; line-height: 1.2; }
.info-titulo-sub { margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap; }
.info-header-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.info-editar {
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25); color: #fff;
  border-radius: 6px; padding: 4px 10px; font-size: 11.5px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
}
.info-editar:hover { background: rgba(255,255,255,.22); }
.info-maximizar {
  background: rgba(255,255,255,.12);
  border: 1px solid rgba(255,255,255,.25);
  color: #fff;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
  display: inline-flex; align-items: center;
  flex-shrink: 0;
}
.info-maximizar:hover { background: rgba(255,255,255,.22); }
.info-badge-crit {
  font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
  letter-spacing: .5px; text-transform: uppercase;
}
.info-badge-CRITICO { background: #3b4766; color: #fff; }
.info-badge-ALTO    { background: rgba(255,255,255,.15); color: #cbd5e1; }
.info-badge-MEDIO   { background: rgba(255,255,255,.1);  color: #94a3b8; }
.info-badge-BAJO    { background: rgba(255,255,255,.08); color: #64748b; }
.info-badge-clave {
  font-size: 9px; font-family: monospace;
  background: rgba(255,255,255,.08); color: #cbd5e1;
  padding: 2px 8px; border-radius: 4px;
}
.info-cerrar {
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2); color: #fff;
  width: 26px; height: 26px; border-radius: 6px;
  font-size: 18px; line-height: 1; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.info-cerrar:hover { background: rgba(255,255,255,.2); }

.info-body {
  padding: 20px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  background: #ffffff !important;
}
.info-modal.maximizado .info-body { padding: 28px 36px; }
.info-loading {
  padding: 24px 16px; text-align: center; color: #64748b; font-size: 13px;
}
.info-seccion { margin-bottom: 14px; }
.info-seccion:last-child { margin-bottom: 0; }
.info-seccion-titulo {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  color: #64748b; letter-spacing: .5px; margin-bottom: 4px;
}
.info-seccion-texto,
.info-seccion p {
  font-size: 13px;
  line-height: 1.5;
  color: #1e293b;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  white-space: normal !important;
}
.info-formula {
  background: #F1F5F9;
  color: #0f172a;
  padding: 10px 12px; border-radius: 6px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 12.5px; line-height: 1.5;
  overflow-x: auto;
  overflow-y: hidden;
  word-wrap: normal;
  white-space: pre;
  max-width: 100%;
  margin: 0;
}
.info-badges { display: flex; flex-wrap: wrap; gap: 6px; }
.info-badge-dep {
  background: #EDE9FE; color: #5B21B6;
  font-size: 11px; font-weight: 600;
  padding: 3px 10px; border-radius: 10px;
  cursor: pointer;
  transition: background .12s, color .12s;
}
.info-badge-dep:hover { background: #5B21B6; color: #fff; }

/* Formulario edición */
.info-form .info-form-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 6px 10px;
  font-size: 12px;
  color: #1e293b;
}
.info-form-grid label {
  align-self: center;
  color: #64748b;
  font-size: 11.5px;
}
.info-form-grid label.top { align-self: start; padding-top: 4px; }
.info-form-grid label.chk {
  display: flex; align-items: center; gap: 6px;
  color: #1e293b; font-size: 12px; cursor: pointer;
}
.info-form-grid input[type="text"],
.info-form-grid select,
.info-form-grid textarea {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  color: #1e293b;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
}
.info-form-grid textarea { resize: vertical; }
.info-form-grid .mono {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 11.5px;
}
.info-pantallas-list {
  display: flex; flex-direction: column; gap: 3px;
  font-size: 11.5px;
  padding-left: 0;
  margin: 0;
  list-style: none;
}
.info-pantallas-list label {
  padding-left: 0;
  margin-left: 0;
}
.info-pantallas-list code {
  font-size: 10.5px; color: #64748b;
  background: transparent;
}

.info-footer {
  border-top: 1px solid #e2e8f0;
  padding: 12px 16px;
  background: #f8fafc !important;
  display: flex !important;
  flex-direction: row !important;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.info-footer-edit { justify-content: flex-end; }
.info-footer-texto {
  font-size: 12px;
  line-height: 1.4;
  color: #475569;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal !important;
  flex: 1;
}
.info-footer-btn {
  background: #1A2744; color: #fff;
  border: none; border-radius: 6px;
  padding: 8px 14px; font-size: 12.5px; font-weight: 600; cursor: pointer;
  transition: background .12s;
  flex-shrink: 0;
}
.info-footer-btn:hover { background: #2d3e66; }

.info-btn-cancel {
  background: #fff; color: #475569;
  border: 1px solid #e2e8f0; border-radius: 6px;
  padding: 7px 14px; font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.info-btn-cancel:hover { background: #f1f5f9; }
.info-btn-cancel:disabled { opacity: .5; cursor: not-allowed; }
.info-btn-save {
  background: #0F6E56; color: #fff;
  border: none; border-radius: 6px;
  padding: 7px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer;
}
.info-btn-save:hover { background: #0d5a47; }
.info-btn-save:disabled { opacity: .6; cursor: not-allowed; }
</style>
